import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { StudySession } from '../../services/api';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Card, SpaceCard } from '../ui/Card';
import { Button } from '../ui/Button';
import { FlowStep } from './NewStudySession';

interface ProgressTrackerProps {
  currentStep: FlowStep;
  session: StudySession;
  onStepChange: (step: FlowStep) => void;
  isRTL: boolean;
}

const ProgressTracker: React.FC<ProgressTrackerProps> = ({
  currentStep,
  session,
  onStepChange,
  isRTL
}) => {
  const { t } = useTranslation();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Helper functions
  const hasAudioUpload = () => Boolean(session?.transcriptions && session.transcriptions.length > 0);
  const hasDocuments = () => Boolean(session?.documents && session.documents.length > 0);
  const hasAnyContent = () => hasAudioUpload() || hasDocuments();
  const hasStudyMaterials = () => Boolean(session?.study_materials && session.study_materials.length > 0);
  const hasQuestions = () => Boolean(session?.questions && session.questions.length > 0);
  
  const getQuestionsCompletionStatus = () => {
    if (!session?.questions || session.questions.length === 0) return false;
    // This would need to be calculated based on actual question results
    // For now, just return false unless session is completed
    return !!session.completed_at;
  };

  const steps = [
    { 
      id: 'upload', 
      title: t('sessions:flow.steps.upload', 'Upload Content'), 
      icon: '/astronaut_upload.png', 
      completed: hasAnyContent() 
    },
    { 
      id: 'materials', 
      title: t('sessions:flow.steps.materials', 'Study Materials'), 
      icon: '/astronaut_reading.png', 
      completed: hasStudyMaterials() 
    },
    { 
      id: 'questions', 
      title: t('sessions:flow.steps.questions', 'Practice Questions'), 
      icon: '/astronaut_practicing.png', 
      completed: hasQuestions() && getQuestionsCompletionStatus() 
    },
    { 
      id: 'complete', 
      title: t('sessions:flow.steps.complete', 'Complete Session'), 
      icon: '/astronaut_winning.png', 
      completed: !!session?.completed_at 
    }
  ];

  // Auto-scroll to current step on mobile
  useEffect(() => {
    if (scrollContainerRef.current && typeof window !== 'undefined' && window.innerWidth < 768) {
      const currentStepIndex = steps.findIndex(step => step.id === currentStep);
      if (currentStepIndex !== -1) {
        const stepElement = scrollContainerRef.current.children[currentStepIndex] as HTMLElement;
        if (stepElement) {
          stepElement.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'nearest', 
            inline: 'center' 
          });
        }
      }
    }
  }, [currentStep, steps]);

  // Navigation functions for mobile
  const navigateToNext = () => {
    const currentStepIndex = steps.findIndex(step => step.id === currentStep);
    if (currentStepIndex < steps.length - 1) {
      const nextStep = steps[currentStepIndex + 1];
      const canNavigate = nextStep.id === 'upload' || 
                        (nextStep.id === 'materials' && (hasAnyContent() || hasStudyMaterials())) ||
                        (nextStep.id === 'questions' && hasStudyMaterials()) ||
                        (nextStep.id === 'complete' && session?.completed_at);
      if (canNavigate) {
        onStepChange(nextStep.id as FlowStep);
      }
    }
  };

  const navigateToPrev = () => {
    const currentStepIndex = steps.findIndex(step => step.id === currentStep);
    if (currentStepIndex > 0) {
      const prevStep = steps[currentStepIndex - 1];
      onStepChange(prevStep.id as FlowStep);
    }
  };

  const currentStepIndex = steps.findIndex(step => step.id === currentStep);
  const hasNext = currentStepIndex < steps.length - 1;
  const hasPrev = currentStepIndex > 0;
  const nextStep = hasNext ? steps[currentStepIndex + 1] : null;
  const canNavigateNext = nextStep && (
    nextStep.id === 'upload' || 
    (nextStep.id === 'materials' && (hasAnyContent() || hasStudyMaterials())) ||
    (nextStep.id === 'questions' && hasStudyMaterials()) ||
    (nextStep.id === 'complete' && session?.completed_at)
  );

  return (
    <SpaceCard className="mb-4 md:mb-6 overflow-hidden relative">
      {/* Cosmic Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-4 right-8 w-16 h-16 bg-gradient-to-br from-muted-purple/20 to-muted-purple/5 rounded-full animate-pulse"></div>
        <div className="absolute bottom-4 left-8 w-8 h-8 bg-gradient-to-br from-muted-purple/20 to-muted-purple/5 rounded-full animate-bounce" style={{ animationDelay: '0.5s' }}></div>
      </div>

      <div className="p-3 md:p-6 relative z-10">
        {/* Mobile: Navigation Header + Carousel */}
        <div className="md:hidden">
          {/* Mobile Navigation Header */}
          <div className={`flex items-center justify-between mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <button
              onClick={navigateToPrev}
              disabled={!hasPrev}
              className={`
                w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300
                ${hasPrev 
                  ? 'bg-muted-purple/10 text-muted-purple hover:bg-muted-purple/20 hover:scale-105' 
                  : 'bg-surface-muted/10 text-surface-muted/50 cursor-not-allowed'
                }
              `}
            >
              {isRTL ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
            </button>

            <div className="text-center">
              <p className="text-sm font-medium text-muted-purple">
                {t('sessions:flow.stepProgress', 'Step {{current}} of {{total}}', { 
                  current: currentStepIndex + 1, 
                  total: steps.length 
                })}
              </p>
              <p className="text-xs text-surface-muted mt-1">
                {t('sessions:flow.tapToNavigate', 'Tap arrows or cards to navigate')}
              </p>
            </div>

            <button
              onClick={navigateToNext}
              disabled={!canNavigateNext}
              className={`
                w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300
                ${canNavigateNext 
                  ? 'bg-muted-purple/10 text-muted-purple hover:bg-muted-purple/20 hover:scale-105' 
                  : 'bg-surface-muted/10 text-surface-muted/50 cursor-not-allowed'
                }
              `}
            >
              {isRTL ? <ChevronLeft className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
            </button>
          </div>

          {/* Mobile Carousel */}
          <div 
            ref={scrollContainerRef}
            className={`flex gap-3 overflow-x-auto snap-x snap-mandatory pb-2 ${isRTL ? 'flex-row-reverse' : ''}`}
            style={{
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              WebkitOverflowScrolling: 'touch'
            }}
          >
            <style>{`
              .progress-carousel::-webkit-scrollbar {
                display: none;
              }
            `}</style>
            {steps.map((step, index) => {
              const isActive = currentStep === step.id;
              const canNavigate = step.id === 'upload' || 
                                (step.id === 'materials' && (hasAnyContent() || hasStudyMaterials())) ||
                                (step.id === 'questions' && hasStudyMaterials()) ||
                                (step.id === 'complete' && session?.completed_at);

              return (
                <div
                  key={step.id}
                  onClick={() => canNavigate && onStepChange(step.id as FlowStep)}
                  className={`
                    flex-shrink-0 w-[320px] snap-center transition-all duration-300
                    ${canNavigate ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}
                    ${isActive ? 'scale-105' : 'scale-95'}
                  `}
                >
                  {/* Mobile Step Card */}
                  <div className={`
                    relative bg-white/90 backdrop-blur-lg rounded-xl p-4 border transition-all duration-300
                    ${isActive 
                      ? 'border-muted-purple/50 bg-gradient-to-br from-muted-purple/15 to-muted-purple/5 shadow-lg shadow-muted-purple/20' 
                      : step.completed
                      ? 'border-green-500/30 bg-gradient-to-br from-green-500/10 to-green-400/5 shadow-md'
                      : 'border-surface-muted/30'
                    }
                  `}>
                    
                    {/* Mobile Layout */}
                    <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                      {/* Step Number */}
                      <div className={`
                        w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0
                        ${isActive 
                          ? 'bg-muted-purple text-white' 
                          : step.completed 
                          ? 'bg-green-500 text-white'
                          : 'bg-surface-muted/30 text-gray-700'
                        }
                      `}>
                        {index + 1}
                      </div>

                      {/* Step Icon */}
                      <div className={`
                        w-14 h-14 rounded-lg flex items-center justify-center transition-all duration-300 flex-shrink-0
                        ${isActive 
                          ? 'bg-gradient-to-br from-muted-purple to-muted-purple/90 shadow-lg' 
                          : step.completed 
                          ? 'bg-gradient-to-br from-green-500 to-green-400 shadow-md'
                          : 'bg-gradient-to-br from-surface-muted/20 to-surface-muted/10'
                        }
                      `}>
                        <img 
                          src={step.icon} 
                          alt={step.title}
                          className="w-10 h-10 object-contain"
                        />
                      </div>

                      {/* Step Content */}
                      <div className={`flex-1 min-w-0 ${isRTL ? 'text-right' : 'text-left'}`}>
                        <h3 className={`
                          font-bold text-sm leading-tight transition-colors duration-300 truncate
                          ${isActive 
                            ? 'text-muted-purple' 
                            : step.completed 
                            ? 'text-green-700'
                            : 'text-gray-800'
                          }
                        `}>
                          {step.title}
                        </h3>
                      </div>
                    </div>

                    {/* Progress Line for mobile */}
                    {index < steps.length - 1 && (
                      <div className={`
                        absolute top-1/2 -translate-y-1/2 w-3 h-0.5 bg-gradient-to-r
                        ${isRTL ? '-left-3' : '-right-3'}
                        ${step.completed ? 'from-green-500/60 to-green-500/30' : 'from-surface-muted/30 to-surface-muted/10'}
                      `} />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* Mobile Progress Dots */}
          <div className={`flex justify-center gap-2 mt-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
            {steps.map((step, index) => {
              const isActive = currentStep === step.id;
              return (
                <div
                  key={step.id}
                  className={`
                    w-2 h-2 rounded-full transition-all duration-300
                    ${isActive 
                      ? 'bg-muted-purple w-6' 
                      : step.completed 
                      ? 'bg-green-500'
                      : 'bg-surface-muted/30'
                    }
                  `}
                />
              );
            })}
          </div>
        </div>

        {/* Desktop: Grid Layout (Original) */}
        <div className={`hidden md:grid md:grid-cols-4 gap-4 ${isRTL ? 'md:grid-flow-col-reverse' : ''}`} dir={isRTL ? 'rtl' : 'ltr'}>
          {steps.map((step, index) => {
            const isActive = currentStep === step.id;
            const canNavigate = step.id === 'upload' || 
                              (step.id === 'materials' && (hasAnyContent() || hasStudyMaterials())) ||
                              (step.id === 'questions' && hasStudyMaterials()) ||
                              (step.id === 'complete' && session?.completed_at);

            return (
              <div
                key={step.id}
                onClick={() => canNavigate && onStepChange(step.id as FlowStep)}
                className={`
                  relative group cursor-pointer transition-all duration-300 transform
                  ${canNavigate ? 'hover:scale-105 hover:-translate-y-1' : 'cursor-not-allowed opacity-60'}
                  ${isActive ? 'scale-105' : ''}
                `}
              >
                {/* Desktop Step Card */}
                <div className={`
                  relative bg-white/80 backdrop-blur-lg rounded-xl p-4 border transition-all duration-300
                  ${isActive 
                    ? 'border-muted-purple/50 bg-gradient-to-br from-muted-purple/10 to-muted-purple/5 shadow-lg shadow-muted-purple/20' 
                    : step.completed
                    ? 'border-green-500/30 bg-gradient-to-br from-green-500/10 to-green-400/5 shadow-md'
                    : 'border-surface-muted/30 hover:border-muted-purple/50'
                  }
                `}>
                  
                  {/* Step Number & Status */}
                  <div className={`flex items-center justify-between mb-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <div className={`
                      w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold
                      ${isActive 
                        ? 'bg-muted-purple text-white' 
                        : step.completed 
                        ? 'bg-green-500 text-white'
                        : 'bg-surface-muted/30 text-gray-700'
                      }
                    `}>
                      {index + 1}
                    </div>
                  </div>

                  {/* Step Icon */}
                  <div className="flex justify-center mb-3">
                    <div className={`
                      w-16 h-16 rounded-xl flex items-center justify-center transition-all duration-300 group-hover:rotate-6
                      ${isActive 
                        ? 'bg-gradient-to-br from-muted-purple to-muted-purple/90 shadow-lg animate-pulse' 
                        : step.completed 
                        ? 'bg-gradient-to-br from-green-500 to-green-400 shadow-md'
                        : 'bg-gradient-to-br from-surface-muted/20 to-surface-muted/10 group-hover:from-muted-purple/20 group-hover:to-muted-purple/10'
                      }
                    `}>
                      <img 
                        src={step.icon} 
                        alt={step.title}
                        className="w-12 h-12 object-contain"
                      />
                    </div>
                  </div>

                  {/* Step Title */}
                  <div className={`text-center ${isRTL ? 'text-right' : 'text-left'}`} dir={isRTL ? 'rtl' : 'ltr'}>
                    <h3 className={`
                      font-bold text-sm mb-1 transition-colors duration-300
                      ${isActive 
                        ? 'text-muted-purple' 
                        : step.completed 
                        ? 'text-green-700'
                        : 'text-gray-800 group-hover:text-muted-purple'
                      }
                    `}>
                      {step.title}
                    </h3>
                  </div>

                  {/* Connection Line */}
                  {index < steps.length - 1 && (
                    <div className={`
                      hidden md:block absolute top-1/2 -translate-y-1/2 w-6 h-0.5 bg-gradient-to-r from-current to-transparent
                      ${isRTL ? '-left-6' : '-right-6'}
                      ${step.completed ? 'text-green-500' : 'text-surface-muted/30'}
                    `}>
                      <div className={`
                        absolute top-1/2 -translate-y-1/2 text-sm
                        ${isRTL ? '-left-4' : '-right-4'}
                      `}>
                        {isRTL ? '←' : '→'}
                      </div>
                    </div>
                  )}

                  {/* Hover Glow Effect */}
                  {canNavigate && (
                    <div className={`
                      absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none
                      ${isActive 
                        ? 'bg-gradient-to-br from-muted-purple/20 to-muted-purple/5' 
                        : 'bg-gradient-to-br from-muted-purple/10 to-muted-purple/5'
                      }
                    `}></div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </SpaceCard>
  );
};

export default ProgressTracker; 